/**
 * Idle Timeout Handler Component
 * Tracks user activity and logs out after inactivity period
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

interface IdleTimeoutHandlerProps {
  timeout?: number; // Inactivity timeout in minutes (default: 10)
  warningTime?: number; // Warning before logout in seconds (default: 60)
}

const IdleTimeoutHandler: React.FC<IdleTimeoutHandlerProps> = ({
  timeout = 10,
  warningTime = 60,
}) => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(warningTime);

  const timeoutMs = timeout * 60 * 1000; // Convert to milliseconds
  const warningMs = warningTime * 1000;

  // Events to track for activity
  const activityEvents = [
    'mousedown',
    'mousemove',
    'keydown',
    'scroll',
    'touchstart',
    'click',
  ];

  // Handle logout
  const handleLogout = useCallback(() => {
    setShowWarning(false);
    logout();
    navigate('/login', {
      state: { message: 'You have been logged out due to inactivity.' }
    });
  }, [logout, navigate]);

  // Reset activity timer
  const resetTimer = useCallback(() => {
    // If warning is showing and user is active, dismiss the warning
    if (showWarning) {
      setShowWarning(false);
      setCountdown(warningTime);
    }

    // Store last activity time
    localStorage.setItem('lastActivity', Date.now().toString());
  }, [showWarning, warningTime]);

  // Stay logged in (user clicked button)
  const handleStayLoggedIn = () => {
    setShowWarning(false);
    setCountdown(warningTime);
    localStorage.setItem('lastActivity', Date.now().toString());
  };

  // Check for inactivity
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear lastActivity when logged out
      localStorage.removeItem('lastActivity');
      return;
    }

    // Always reset last activity on mount (fresh login)
    localStorage.setItem('lastActivity', Date.now().toString());

    // Check inactivity periodically
    const checkInactivity = () => {
      const lastActivity = parseInt(localStorage.getItem('lastActivity') || Date.now().toString());
      const now = Date.now();
      const elapsed = now - lastActivity;

      if (elapsed >= timeoutMs - warningMs && elapsed < timeoutMs) {
        // Show warning
        if (!showWarning) {
          setShowWarning(true);
          setCountdown(Math.ceil((timeoutMs - elapsed) / 1000));
        }
      } else if (elapsed >= timeoutMs) {
        // Timeout reached - logout
        handleLogout();
      }
    };

    const intervalId = setInterval(checkInactivity, 1000);

    // Add activity listeners
    activityEvents.forEach((event) => {
      window.addEventListener(event, resetTimer, { passive: true });
    });

    // Handle tab visibility
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkInactivity();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      activityEvents.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, timeoutMs, warningMs, showWarning, handleLogout, resetTimer]);

  // Countdown timer when warning is shown
  useEffect(() => {
    if (!showWarning) return;

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          handleLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [showWarning, handleLogout]);

  if (!isAuthenticated) return null;

  return (
    <Dialog
      open={showWarning}
      onClose={handleStayLoggedIn}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon color="warning" />
        Session Timeout Warning
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          You will be logged out due to inactivity in:
        </Typography>
        <Box sx={{ textAlign: 'center', my: 3 }}>
          <Typography variant="h2" color="warning.main" fontWeight={700}>
            {countdown}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            seconds
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={(countdown / warningTime) * 100}
          color="warning"
          sx={{ height: 8, borderRadius: 4 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          Click "Stay Logged In" or move your mouse to continue working.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleLogout} color="inherit">
          Logout Now
        </Button>
        <Button onClick={handleStayLoggedIn} variant="contained" autoFocus>
          Stay Logged In
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IdleTimeoutHandler;
